import type { NewCampaignFormSchema } from 'c-main/Types';
import { CampaignFormNames } from 'c-main/Types';
import { NewCampaignStep, newCampaignStepCount } from './newCampaignSteps';

/**
 * The in-progress new-campaign form, kept in sessionStorage so a refresh lands back on the same
 * step with the same input. Per tab, per user and per form (new vs cloning a given campaign); it
 * goes once the campaign is created or the user starts over. Storage can be missing or throw
 * (private windows, blocked site data), in which case the wizard simply starts fresh.
 */

export type NewCampaignDraft = {
    step: NewCampaignStep;
    values: Partial<NewCampaignFormSchema>;
};

const storageKey = (key: string) => `newCampaignDraft:${key}`;

const clampStep = (step: number): NewCampaignStep =>
    Math.min(Math.max(Math.trunc(step), 0), newCampaignStepCount - 1);

const hasText = (value: unknown) => typeof value === 'string' && value.trim() !== '';

/**
 * Whether there is anything worth restoring. The form writes values on its own as it opens (the
 * only team a user belongs to, the line items' dates), which alone is not work to bring back.
 */
export const isWorthRestoring = (draft: NewCampaignDraft) =>
    draft.step > NewCampaignStep.Details ||
    [
        CampaignFormNames.name,
        CampaignFormNames.reference,
        CampaignFormNames.client_name,
        CampaignFormNames.start,
    ].some(field => hasText(draft.values[field])) ||
    (draft.values.line_items?.length ?? 0) > 0;

export const loadNewCampaignDraft = (key: string): NewCampaignDraft | null => {
    try {
        const raw = window.sessionStorage.getItem(storageKey(key));
        if (raw == null) return null;

        const parsed = JSON.parse(raw);
        if (
            typeof parsed?.step !== 'number' ||
            !Number.isFinite(parsed.step) ||
            parsed.values == null ||
            typeof parsed.values !== 'object' ||
            Array.isArray(parsed.values)
        ) {
            return null;
        }

        const draft = { step: clampStep(parsed.step), values: parsed.values };
        return isWorthRestoring(draft) ? draft : null;
    } catch {
        return null;
    }
};

export const saveNewCampaignDraft = (key: string, draft: NewCampaignDraft) => {
    try {
        window.sessionStorage.setItem(storageKey(key), JSON.stringify(draft));
    } catch {
        // full or blocked storage: the wizard still works, it just won't survive a refresh
    }
};

export const clearNewCampaignDraft = (key: string) => {
    try {
        window.sessionStorage.removeItem(storageKey(key));
    } catch {
        // nothing to clear
    }
};
